import { Client } from "@/Common/Api/Client";
import { Combobox } from "@/Common/Components/Inputs/Combobox";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { DateTime } from "@/Common/Helpers/DateTime";
import { IndividualIdentityHelper } from "@/Common/Helpers/IndividualIdentityHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { PreferredPronouns } from "@/Common/Models/Individuals/Enums/PreferredPronouns";
import { ExpressEditMode } from "@/Common/Models/Patient/PatientEnums";
import { IPatientDemographicsModel } from "@/Common/Models/Patient/UI/Profile/IPatientDemographicsModel";
import { ReligionCache } from "@/Common/Models/Patient/UI/Profile/ReligionModel";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { Ethnicity } from "@/Common/Models/UI/Ethnicity";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { EthnicitySearchAutocompleteCustom } from "./EthnicitySearchAutocompleteCustom";

interface IPatientProfileDemographicsProps {
    model: IPatientDemographicsModel;
    countryLookupId: string;
    autofocusOperation?: ExpressEditMode;
}

class DemographicEditState {
    public employersCache: Immutable.List<SearchableItemModelT<string>>;
    public isloadingCache: boolean;
}

class DemographicEdit extends React.Component<IPatientProfileDemographicsProps, DemographicEditState> {
    public validationHelper = new ValidationHelper();
    public mexicoCountryLookup = cacheStore.FindLookupByLookupCode("CNMEXICO");
    public isSSNValid = false;

    constructor(props: IPatientProfileDemographicsProps) {
        super(props);

        const initialState = new DemographicEditState();
        initialState.isloadingCache = false;
        if (cacheStore.employersCache != null)
            initialState.employersCache = cacheStore.employersCache;
        this.state = initialState;
    }

    public render(): JSX.Element {
        return (
            <div className="scrollable patientProfileView patient-prof" data-cy="patientProfile-view">
                <div className="block separated">
                    <div className="heading">
                        <h3 className="thin-font">{cacheStore.GetString("PatientInformationLabel")}</h3>
                    </div>
                    <div className="patient-profile fxd-wdth-container">
                        <div className="tb">
                            {this.leftSide(this.props.model)}
                            {this.rightSide(this.props.model)}
                        </div>
                    </div>
                </div>
                <div className="block separated">
                    <div className="heading">
                        <h3 className="thin-font">{cacheStore.GetString("ParentsLabel")}</h3>
                    </div>
                    <div className="patient-profile fxd-wdth-container">
                        {this.renderParents()}
                    </div>
                </div>
            </div>
        );
    }

    private renderParents(): JSX.Element {
        const model = this.props.model;

        return (
            <div className="tb">
                <div className="tbc left">
                    <div>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="c1">
                                        <label className="form-label">{cacheStore.GetString("MotherLastNameLabel")}</label>
                                    </td>
                                    <td className="c2">
                                        <TextboxControl dataCy="patientProfile-parents-mothersLastName" value={model.get("MothersLastName")} className="form-control" operation={LocalPatientOperations.Profile.Demographics_MothersLastNameChange} />
                                    </td>
                                </tr>

                                <tr>
                                    <td className="c1">
                                        <label className="form-label">{cacheStore.GetString("MotherFirstNameLabel")}</label>
                                    </td>
                                    <td className="c2">
                                        <TextboxControl dataCy="patientProfile-parents-mothersFirstName" value={model.get("MothersFirstName")} className="form-control" operation={LocalPatientOperations.Profile.Demographics_MothersFirstNameChange} />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="tbc left">
                    <div>
                        <table>
                            <tbody>

                                <tr>
                                    <td className="c1">
                                        <label className="form-label">{cacheStore.GetString("FatherLastNameLabel")}</label>
                                    </td>
                                    <td className="c2">
                                        <TextboxControl
                                            dataCy="patientProfile-parents-fathersLastName"
                                            value={model.get("FathersLastName")}
                                            className="form-control"
                                            operation={LocalPatientOperations.Profile.Demographics_FathersLastNameChange}
                                        />
                                    </td>
                                </tr>

                                <tr>
                                    <td className="c1">
                                        <label className="form-label">{cacheStore.GetString("FatherFirstNameLabel")}</label>
                                    </td>
                                    <td className="c2">
                                        <TextboxControl
                                            dataCy="patientProfile-parents-fathersFirstName"
                                            value={model.get("FathersFirstName")}
                                            className="form-control"
                                            operation={LocalPatientOperations.Profile.Demographics_FathersFirstNameChange}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    private leftSide(model: IPatientDemographicsModel): JSX.Element {
        // fetch mask info for ssn
        var validationRule = this.validationHelper.getSSNValidationRule(this.props.countryLookupId);
        var mask: string = validationRule ? validationRule.EditMaskPattern : "";
        var validationErrorMessage: string = (validationRule && validationRule.ValidationErrorMessageLookupId) ? cacheStore.GetLocalizedValueByLookupId(validationRule.ValidationErrorMessageLookupId) : "";

        this.isSSNValid = (model.get("IsSSNValid") == null || model.get("IsSSNValid") == undefined) ? true : model.get("IsSSNValid");

        let today = DateTime.nowMoment();

        let nameTableRow = (label: string, dataCyLabel: string, op: number, value?: string): JSX.Element => (
            <tr>
                <td className="c1">
                    <label className="form-label">{cacheStore.GetString(`${label}Label`)}</label>
                </td>
                <td className="c2">
                    <TextboxControl id={`${label}Edit`} dataCy={`patientProfile-patientInfo-${dataCyLabel}`} value={value} className="form-control" operation={op} />
                </td>
            </tr>
        );

        return (
            <div className="tbc left">
                <div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("PrefixLabel")}</label>
                                </td>
                                <td className="c2">
                                    <SelectLookupControl
                                        dataCy="patientProfile-patientInfo-prefix"
                                        IncludeEmptyChoice={true}
                                        Value={model.get("SelectedPrefixLookupId")}
                                        AvailableValues={model.get("AvailablePrefixLookupIds").toArray().map(prefix => prefix.ToComboboxItem())}
                                        Operation={LocalPatientOperations.Profile.Demographics_SelectPrefix}
                                    />
                                </td>
                            </tr>

                            {nameTableRow("FirstName", "firstName", LocalPatientOperations.Profile.Demographics_FirstNameChange, model.get("FirstName"))}
                            {nameTableRow("LastName", "lastname", LocalPatientOperations.Profile.Demographics_LastNameChange, model.get("LastName"))}
                            {nameTableRow("MiddleName", "middleName", LocalPatientOperations.Profile.Demographics_MiddleNameChange, model.get("MiddleName"))}
                            {nameTableRow("PreferredName", "PreferredName", LocalPatientOperations.Profile.Demographics_PreferredNameChange, model.get("PreferredName"))}

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("SuffixLabel")}</label>
                                </td>
                                <td className="c2">
                                    <SelectLookupControl
                                        dataCy="patientProfile-patientInfo-suffix"
                                        IncludeEmptyChoice={true}
                                        Value={model.get("SelectedSuffixLookupId")}
                                        AvailableValues={model.get("AvailableSuffixLookupIds").toArray().map(x => x.ToComboboxItem())}
                                        Operation={LocalPatientOperations.Profile.Demographics_SelectSuffix}
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("PreferredPronounsLabel")}</label>
                                </td>
                                <td className="c2">
                                    <SelectLookupControl
                                        dataCy="patientProfile-patientInfo-preferredPronouns"
                                        IncludeEmptyChoice={true}
                                        Value={model.get("PreferredPronouns")?.toString()}
                                        AvailableValues={IndividualIdentityHelper.getAvailablePreferredPronouns()}
                                        Operation={LocalPatientOperations.Profile.Demographics_SelectPreferredPronouns}
                                    />
                                </td>
                            </tr>
                            {model.get("PreferredPronouns") == PreferredPronouns.Other && (
                                <tr>
                                    <td />
                                    <td className="c2">
                                        <TextboxControl
                                            id="preferredPronoun_Other"
                                            value={model.get("SpecifiedPreferredPronouns")}
                                            operation={LocalPatientOperations.Profile.Demographics_SpecifiedPreferredPronounsChange}
                                            maxLength={100}
                                            focus={model.get("PreferredPronouns") == PreferredPronouns.Other && !model.get("SpecifiedPreferredPronouns")}
                                        />
                                    </td>
                                </tr>
                            )}
                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("SocialInsuranceNumberLabel")}</label>
                                </td>
                                <td className="c2">
                                    <TextboxMaskedControl isValid={this.isSSNValid} id="ssn_maskedbox" dataCy="patientProfile-patientInfo-ssn" value={model.get("SocialInsuranceNumber")} onChangeOperation={LocalPatientOperations.Profile.Demographics_SocialInsuranceChange} item={null} mask={mask} validationErrorMessage={validationErrorMessage} />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("SexAtBirthLabel")}</label>
                                </td>
                                <td className="c2">
                                    <SelectLookupControl
                                        dataCy="patientProfile-patientInfo-sexAtBirth"
                                        IncludeEmptyChoice={true}
                                        Value={model.get("SelectedGenderLookupId")}
                                        AvailableValues={model
                                            .get("AvailableGenderLookupIds")
                                            .toArray()
                                            .map(x => x.ToComboboxItem())
                                            .sortByProperty(p => p.DisplayName)
                                        }
                                        Operation={LocalPatientOperations.Profile.Demographics_GenderSelect}
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("GenderIdentityLabel")}</label>
                                </td>
                                <td className="c2">
                                    <SelectLookupControl
                                        dataCy="patientProfile-patientInfo-genderIdentity"
                                        Value={model.get("GenderIdentity")?.toString() ?? ""}
                                        AvailableValues={IndividualIdentityHelper.getAllGenderIdentitiesAsComboBox()}
                                        Operation={LocalPatientOperations.Profile.Demographics_GenderIdentityChange}
                                    />

                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("EthnicityLabel")}</label>
                                </td>
                                <td className="c2">
                                    <EthnicitySearchAutocompleteCustom
                                        componentId="individual_ethnicity"
                                        selectedItemList={Ethnicity.selectedValuesOf(model.get("SelectedEthnicityKeys"))}
                                        disabled={false}
                                        selectionOp={LocalPatientOperations.Profile.Demographics_EthnicitySelect}
                                        clearOp={LocalPatientOperations.Profile.Demographics_EthnicityClear}
                                        allowMultipleItemSelection
                                        closeDropDownAfterSelection
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("DateofBirthLabel")}</label>
                                </td>
                                <td className="c2">
                                    <DateSelector
                                        id="dob_dt"
                                        dataCy="patientProfile-patientInfo-dt"
                                        disabled={false}
                                        date={model.get("DateOfBirth")}
                                        dateFormat="yyyy-MM-dd"
                                        onSelectOp={LocalPatientOperations.Profile.Demographics_DateOfBirthChange}
                                        focus={this.props.autofocusOperation === ExpressEditMode.DOB}
                                        maxDate={today.toDate()}
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("ReligionLabel")}</label>
                                </td>
                                <td className="c2">
                                    <Combobox
                                        HasEmptyItem={true}
                                        id="_TemplateComboBox"
                                        dataCy="patientProfile-patientInfo-religions"
                                        items={this.props.model.get("Religions").toArray().map(x => ReligionCache.toComboboxItem(x)).toImmutable()}
                                        selectedCode={this.props.model.get("Religion") != null && this.props.model.get("Religion").Model != null ? this.props.model.get("Religion").Model.Code : ""}
                                        onChange={this.onReligionChange.bind(this)}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private rightSide(model: IPatientDemographicsModel): JSX.Element {
        return (
            <div className="tbc right">
                <div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("MaritalStatusDLabel")}</label>
                                </td>
                                <td className="c2">
                                    <SelectLookupControl
                                        dataCy="patientProfile-patientInfo-legalStatus"
                                        IncludeEmptyChoice={true}
                                        Value={model.get("SelectedLegalStatusLookupId")}
                                        AvailableValues={model.get("AvailableLegalStatusLookupIds").toArray().map(x => x.ToComboboxItem())}
                                        Operation={LocalPatientOperations.Profile.Demographics_LegalStatusSelect}
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("PreferredOfficialLanguageLabel")}</label>
                                </td>
                                <td className="c2">
                                    <SelectLookupControl
                                        dataCy="patientProfile-patientInfo-officialLanguage"
                                        IncludeEmptyChoice={true}
                                        Value={model.get("SelectedOfficialLanguageLookupId")}
                                        AvailableValues={model.get("AvailableOfficialLanguageLookupIds")
                                            .toArray()
                                            .map(x => x.ToComboboxItem())
                                            .sortByProperty(p => p.DisplayName)
                                            .sortByProperty(p => p.Position)
                                        }
                                        Operation={LocalPatientOperations.Profile.Demographics_OfficialLanguageSelect}
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("PreferredSpokenLanguageLabel")}</label>
                                </td>
                                <td className="c2">
                                    <SelectLookupControl
                                        dataCy="patientProfile-patientInfo-spokenLanguage"
                                        IncludeEmptyChoice={true}
                                        Value={model.get("SelectedSpokenLanguageLookupId")}
                                        AvailableValues={model.get("AvailableSpokenLanguageLookupIds")
                                            .toArray()
                                            .map(x => x.ToComboboxItem())
                                            .sortByProperty(p => p.DisplayName)
                                            .sortByProperty(p => p.Position)
                                        }
                                        Operation={LocalPatientOperations.Profile.Demographics_SpokenLanguageSelect}
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("PatientStatusLabel")}</label>
                                </td>
                                <td className="c2">
                                    <SelectLookupControl
                                        dataCy="patientProfile-patientInfo-patientStatus"
                                        IncludeEmptyChoice={true}
                                        Value={model.get("SelectedPatientStatusLookupId")}
                                        AvailableValues={model.get("AvailablePatientStatusLookupIds").toArray().map(x => x.ToComboboxItem())}
                                        Operation={LocalPatientOperations.Profile.Demographics_PatientStatusSelect}
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("PatientStatusDateLabel")}</label>
                                </td>
                                <td className="c2">
                                    <DateSelector
                                        id="patientStatut_dt"
                                        dataCy="patientProfile-patientInfo-patientStatus-dt"
                                        disabled={false}
                                        date={model.get("PatientStatusDate")}
                                        dateFormat="yyyy-MM-dd"
                                        onSelectOp={LocalPatientOperations.Profile.Demographics_PatientStatusDateChange}
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("ProfessionLabel")}</label>
                                </td>
                                <td className="c2">
                                    <TextboxControl dataCy="patientProfile-patientInfo-profession" value={model.get("Profession")} className="form-control" operation={LocalPatientOperations.Profile.Demographics_ProfessionChange} />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("EmployerLabel")}</label>
                                </td>
                                <td className="c2">
                                    <TextboxControl
                                        id="EmployerTextBox"
                                        dataCy="patientProfile-patientInfo-employer"
                                        autoCompleteItems={this.state.employersCache}
                                        value={model.get("Employer")}
                                        className="form-control"
                                        RemoveStyle={true}
                                        onAutoCompleteSelectCallBack={(c, m, value) => this.onOrganisationChange(value)}
                                        onChange={value => this.onOrganisationChange(value)}
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <label className="form-label">{cacheStore.GetString("NotesLabel")}</label>
                                </td>
                                <td className="c2">
                                    <TextAreaResizable
                                        id={"patientprofilenote" + model.get("PatientContactId")}
                                        dataCy="patientProfile-patientInfo-notes"
                                        isDisabled={false}
                                        onChange={value => this.onNotesChange(value)}
                                        text={model.get("Notes")}
                                        verticalResizeOnly={true}
                                        setCursorStartOnFocus={false}
                                        resetTextOnIdChange={true}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
        let item: JQuery;

        // auto focus on DOB if we are here from a banner label click
        if (this.props.autofocusOperation == ExpressEditMode.DOB)
            item = $("#dob_dt");
        else if (this.props.autofocusOperation == ExpressEditMode.PatientFirstName)
            item = $("#firstNameEdit");
        else if (this.props.autofocusOperation == ExpressEditMode.PatientLastName)
            item = $("#lastNameEdit");

        if (item)
            item.focus();
    }

    private mutateState(mutation: (s: DemographicEditState) => void, then: any = null): void {
        const s = new DemographicEditState();
        mutation(s);
        this.setState(s, then);
    }

    public onOrganisationChange(value: string): void {
        const self = this;
        if (self.state.employersCache == null)
            this.loadEmployerCache();

        sessionStore.dispatch(LocalPatientOperations.Profile.Demographics_EmployerChange, { value: value });
    }

    public loadEmployerCache(): void {
        const self = this;
        if (!self.state.isloadingCache) {
            Client.Post(
                ServerOperations.Server.Myle_LoadEmployerOrganisationCache,
                {},
                response => {
                    const s = new DemographicEditState();
                    s.isloadingCache = false;
                    if (response.Errors != null && response.Errors.length > 0) {
                        cacheStore.employersCache = null;
                    }
                    else {
                        const employers: string[] = response.employers;
                        if (employers != null) {
                            const emps = employers.map(x => new SearchableItemModelT<string>(x)).toImmutable();
                            cacheStore.employersCache = emps;
                            s.employersCache = cacheStore.employersCache;
                        }
                    }
                    self.setState(s, () => {
                        const input = $("#EmployerTextBox");
                        input.focus();
                        input.moveCursorToEnd();
                    });
                },
                () => self.mutateState(s => s.isloadingCache = false)
            );

            this.mutateState(s => s.isloadingCache = true);
        }
    }

    public onReligionChange(e: string): void {
        const op = new LocalEvent(LocalPatientOperations.Profile.Demographics_ReligionChange);
        op.AddItem("code", e);
        LocalEventDispatcher.dispatch(op);
    }

    public bindProperty(param: string, value: string): void {
        const operation = new LocalEvent(LocalPatientOperations.Profile.Demographics_Binding);
        operation.AddItem("param", param);
        operation.AddItem("value", value);
        LocalEventDispatcher.dispatch(operation);
    }

    public onNotesChange(value: string): void {
        const operation = new LocalEvent(LocalPatientOperations.Profile.Demographics_NotesChange);
        operation.AddItem("value", value);
        LocalEventDispatcher.dispatch(operation);
    }
}

export { DemographicEdit, IPatientProfileDemographicsProps };
